import {
  useLazyGetDraftsQuery,
  useLazyGetModificationsQuery,
} from '@carboncopyco/data';
import { useEffect, useRef, useState } from 'react';

interface Props {
  organizationId: string;
  currentUserEmail: string;
  demoId: string;
  shouldRunInterval: boolean;
  onDemoLock?: (lockedBy: string) => void;
}

const INTERVAL = 10 * 1000; // 10sec
const LAST_MODIFICATION_TIME_OFFSET = 60 * 1000; // 60sec

function lockTimeoutPassed(timestamp) {
  // Convert the Unix timestamp to milliseconds
  const timestampInMs = timestamp * 1000;

  const timestampWithAddition = timestampInMs + LAST_MODIFICATION_TIME_OFFSET;

  const currentTime = Date.now();

  return currentTime > timestampWithAddition;
}

/**
 * This hook is used to check if the demo is locked (edited atm) by another user
 */
export const useIsDemoLocked = ({
  organizationId,
  currentUserEmail,
  demoId,
  shouldRunInterval,
  onDemoLock,
}: Props) => {
  const intervalRef = useRef(null);
  const [userName, setUserName] = useState(null);

  const [fetchModifications] = useLazyGetModificationsQuery();
  const [getDrafts] = useLazyGetDraftsQuery();

  const checkIsDemoLocked = async (): Promise<string | null> => {
    const drafts = await getDrafts({
      organizationId,
      sourceId: Number(demoId),
    });
    const draft = drafts.data?.[0];

    if (!draft) {
      return null;
    }

    return fetchModifications({
      organizationId,
      entityType: 'draft',
      entityId: String(draft.id),
      apiVersion: 2,
    })
      .unwrap()
      .then((result) => {
        if (!result.length) {
          return null;
        }

        const { creator, createTime } = result[result.length - 1];

        if (
          creator !== currentUserEmail &&
          !lockTimeoutPassed(createTime) &&
          draft.modified
        ) {
          setUserName(creator);

          return creator;
        }

        return null;
      })
      .catch((e) => {
        console.error(e);
        return null;
      });
  };

  const handleLockedDemo = async () => {
    const lockedBy = await checkIsDemoLocked();

    if (!lockedBy) {
      return;
    }

    onDemoLock?.(lockedBy);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  useEffect(() => {
    if (!shouldRunInterval) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    handleLockedDemo();

    intervalRef.current = setInterval(() => {
      handleLockedDemo();
    }, INTERVAL);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [shouldRunInterval]);

  return { userName, checkIsDemoLocked };
};
